import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '../lib/cx';

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  /** Full name — initials are derived when no image is given. */
  name?: string;
  src?: string;
  /** Shown instead of initials. For subjects with no name worth abbreviating —
   *  an unknown caller, a withheld number — where initials would be noise. */
  icon?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

function initials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]![0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]![0] ?? '' : '';
  return (first + last).toUpperCase();
}

/** Initials avatar (falls back from an image). */
export function Avatar({ name, src, icon, size = 'md', className, ...rest }: AvatarProps) {
  return (
    <span
      className={cx('tc-avatar', size !== 'md' && `tc-avatar--${size}`, className)}
      role="img"
      aria-label={name ? `${name} avatar` : 'avatar'}
      {...rest}
    >
      {src ? <img src={src} alt="" /> : (icon ?? initials(name))}
    </span>
  );
}
